// Last open project and story, so a refresh of a page with no story in
// the URL can come back to the same book. Cleared only on a deliberate
// exit or when the server says that project is gone.

export const LAST_PROJECT_ID_KEY = 'brainstormy.jetage.lastProjectId';
export const LAST_STORY_ID_KEY = 'brainstormy.jetage.lastStoryId';
export const LAST_BOOK_PAGE_KEY = 'brainstormy.jetage.lastBookPage';
export const LAST_USER_ID_KEY = 'brainstormy.jetage.lastUserId';

// This tab first (two tabs can hold two books), then the last book opened in
// any tab, so a closed and reopened browser comes back to it too (run 40:
// Plot Workshop said "Open a book" after the browser restarted).
function read(key) {
  try {
    return sessionStorage.getItem(key) || localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key, value) {
  try {
    sessionStorage.setItem(key, value);
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked: the book just won't be remembered.
  }
}

function remove(key) {
  try {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  } catch {
    // Nothing stored.
  }
}

export function readLastProjectId() {
  return read(LAST_PROJECT_ID_KEY);
}

export function readLastStoryId() {
  return read(LAST_STORY_ID_KEY);
}

export function rememberProjectId(id) {
  if (id == null || id === '') return;
  write(LAST_PROJECT_ID_KEY, String(id));
}

export function rememberStoryId(id) {
  if (id == null || id === '') return;
  write(LAST_STORY_ID_KEY, String(id));
}

export function forgetOpenStory() {
  remove(LAST_STORY_ID_KEY);
  remove(LAST_BOOK_PAGE_KEY);
}

// The page the writer was on inside the open book, so typing the site
// address brings them back to it (run 43: they landed on Projects with the
// book's menu greyed out).
export function rememberBookPage(path) {
  if (!path || path[0] !== '/') return;
  write(LAST_BOOK_PAGE_KEY, path);
}

// Only while that book is still the remembered one.
export function readBookPage() {
  if (!readLastStoryId()) return null;
  const path = read(LAST_BOOK_PAGE_KEY);
  return path && path[0] === '/' ? path : null;
}

export function forgetOpenProject() {
  remove(LAST_PROJECT_ID_KEY);
  remove(LAST_STORY_ID_KEY);
  remove(LAST_BOOK_PAGE_KEY);
}

export function readLastUserId() {
  return read(LAST_USER_ID_KEY);
}

// Book memory is per account. A different account, or a first visit that
// still holds an unstamped book, starts clean. The same account keeps it.
export function rememberSignedInUser(userId) {
  if (userId == null || userId === '') return;
  const id = String(userId);
  if (readLastUserId() === id) return;
  forgetOpenProject();
  write(LAST_USER_ID_KEY, id);
}

// 404 and 403 mean the project or story is not there for this writer.
// A timeout, a 5xx, or a dropped connection is not that.
export function isNotFound(err) {
  const status = Number(err?.status);
  return status === 404 || status === 403;
}
